<template>
  <ImageBar
    title="编辑推荐"
    imageUrl="https://picsum.photos/1200/500?random=1"
    height="80px"
  />
  <div class="blog-container nav-content-inset">
    <div class="blog-content">
      <div class="masonry-grid" v-loading="loading && blogs.length === 0">
        <div
          v-for="blog in blogs"
          :key="blog.id"
          class="masonry-item"
        >
          <el-card
            class="blog-card clickable-card"
            :class="{ 'only-admin-card': blog.onlyAdmin === 1 }"
            shadow="hover"
            @click="readBlog(blog.id)"
          >
            <div class="thumbnail-container">
              <img
                :src="blog.banner || 'https://picsum.photos/300/200?random=' + blog.id"
                class="blog-image"
                :alt="blog.title"
              />
              <el-tag
                v-if="blog.onlyAdmin === 1"
                type="warning"
                size="small"
                class="only-admin-badge"
              >
                Only Admin
              </el-tag>
            </div>
            <div class="blog-info">
              <h3>{{ blog.title }}</h3>
              <div class="blog-meta">
                <span class="author">{{ blog.authors?.join(', ') || '匿名' }}</span>
                <span class="date">{{ formatDate(blog.createdAt) }}</span>
              </div>
              <p class="blog-summary">{{ blog.summary }}</p>
              <div class="blog-tags" v-if="blog.tags?.length">
                <el-tag v-for="tag in blog.tags" :key="tag" size="small" class="tag">{{ tag }}</el-tag>
              </div>
            </div>
          </el-card>
        </div>
      </div>

      <div v-if="loading && blogs.length > 0" class="loading-more">
        <el-skeleton :rows="1" animated />
      </div>
      <div v-else-if="!hasMore && blogs.length > 0" class="no-more">
        没有更多文章了
      </div>
      <div v-else ref="loadMoreTrigger" class="load-more-trigger"></div>
    </div>

    <div v-if="!loading && blogs.length === 0" class="empty-state">
      <el-empty description="暂无编辑推荐" />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import ImageBar from './components/ImageBar.vue'
import {
  listCacheKey,
  readListCache,
  writeListCache
} from '@/utils/listPageCache'

const BLOG_LIST_CACHE_KEY = listCacheKey('blog')

export default {
  name: 'BlogView',
  components: {
    ImageBar
  },
  setup() {
    const router = useRouter()
    const cached = readListCache(BLOG_LIST_CACHE_KEY)
    const blogs = ref(cached?.blogs ? [...cached.blogs] : [])
    const loading = ref(false)
    const hasMore = ref(cached?.hasMore ?? true)
    const currentPage = ref(cached?.currentPage ?? 1)
    const pageSize = ref(12)
    const observer = ref(null)
    const loadMoreTrigger = ref(null)

    const setupObserver = () => {
      if (observer.value) {
        observer.value.disconnect()
        observer.value = null
      }

      nextTick(() => {
        if (!loadMoreTrigger.value || !hasMore.value) return

        observer.value = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && !loading.value && hasMore.value) {
            fetchBlogs(true)
          }
        }, {
          root: null,
          rootMargin: '100px',
          threshold: 0.1
        })

        observer.value.observe(loadMoreTrigger.value)
      })
    }

    const fetchBlogs = async (isLoadMore = false) => {
      if (loading.value) return
      if (isLoadMore && !hasMore.value) return

      try {
        loading.value = true
        const token = localStorage.getItem('jwt_token')
        const page = isLoadMore ? currentPage.value + 1 : 1

        const response = await axios.post('/api/v1/blog/list', {
          page,
          pageSize: pageSize.value
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code === 200) {
          const newBlogs = response.data.data.blogs || []

          if (isLoadMore) {
            blogs.value = [...blogs.value, ...newBlogs]
            currentPage.value = page
          } else {
            blogs.value = newBlogs
            currentPage.value = 1
          }

          hasMore.value = newBlogs.length === pageSize.value
          writeListCache(BLOG_LIST_CACHE_KEY, {
            blogs: blogs.value,
            hasMore: hasMore.value,
            currentPage: currentPage.value
          })
          setupObserver()
        } else {
          ElMessage.error(response.data.message || '获取编辑推荐列表失败')
        }
      } catch (error) {
        console.error('Error fetching blogs:', error)
        ElMessage.error('获取编辑推荐列表失败')
      } finally {
        loading.value = false
      }
    }

    const readBlog = (id) => {
      router.push(`/blogs/${id}`)
    }

    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    onMounted(() => {
      fetchBlogs()
    })

    onUnmounted(() => {
      if (observer.value) {
        observer.value.disconnect()
        observer.value = null
      }
    })

    return {
      blogs,
      loading,
      hasMore,
      loadMoreTrigger,
      readBlog,
      formatDate
    }
  }
}
</script>

<style scoped>
.blog-container {
  max-width: 1800px;
  margin: 0 auto;
  padding: 20px;
}

.blog-content {
  background-color: #fff;
 
}

.masonry-grid {
  column-count: 4;
  column-gap: 16px;
  padding: 0;
}

.masonry-item {
  break-inside: avoid;
  margin-bottom: 16px;
  display: block;
}

.blog-card {
  height: auto;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
}

.blog-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.only-admin-card {
  background-color: var(--el-color-warning-light-9);
  border: 1px solid var(--el-color-warning-light-5);
}

.only-admin-card :deep(.el-card__body) {
  background-color: var(--el-color-warning-light-9);
}

.only-admin-card:hover {
  box-shadow: 0 8px 25px rgba(230, 162, 60, 0.2);
}

.thumbnail-container {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.only-admin-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}

.blog-image {
  width: 100%;
  height: auto;
  min-height: 120px;
  max-height: 280px;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.blog-card:hover .blog-image {
  transform: scale(1.02);
}

.blog-info {
  padding: 15px;
}

.blog-info h3 {
  margin: 0 0 10px 0;
  font-size: 18px;
  color: var(--el-text-color-primary);
  line-height: 1.4;
}

.blog-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  flex-wrap: wrap;
  gap: 6px;
}

.blog-summary {
  color: var(--el-text-color-regular);
  margin-bottom: 12px;
  line-height: 1.5;
  font-size: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.blog-tags {
  margin-bottom: 12px;
}

.tag {
  margin-right: 5px;
  margin-bottom: 5px;
}

.loading-more,
.no-more {
  text-align: center;
  padding: 20px;
  color: var(--el-text-color-secondary);
}

.load-more-trigger {
  height: 1px;
  width: 100%;
}

.empty-state {
  padding: 40px;
  text-align: center;
}

.clickable-card {
  cursor: pointer;
}

@media (max-width: 1200px) {
  .masonry-grid {
    column-count: 3;
  }
}

@media (max-width: 900px) {
  .masonry-grid {
    column-count: 2;
  }
}

@media (max-width: 768px) {
  .blog-container {
    padding: 0;
  }

  .blog-content {
    padding: 0;
  }

  .masonry-grid {
    column-count: 2;
    column-gap: 8px;
  }

  .masonry-item {
    margin-bottom: 8px;
  }
}

@media (max-width: 480px) {
  .masonry-grid {
    column-count: 1;
  }
}
</style>
